<script setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import { useMetaCampaignsFormatters } from '../composables/useMetaCampaignsFormatters';

const props = defineProps({
  campaigns: {
    type: Array,
    default: () => [],
  },
  // { since, until } in unix seconds — carried into the detail page's URL so
  // clicking a campaign never silently switches off the period being viewed.
  period: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { formatFullNumber } = useNumberFormatter();
const { brl, roasClass } = useMetaCampaignsFormatters();

const goToDetail = campaign => {
  router.push(
    accountScopedRoute(
      'meta_campaigns_campaign_detail',
      { campaignId: campaign.id },
      props.period
    )
  );
};
</script>

<template>
  <div class="rounded-xl border border-n-strong bg-n-alpha-3 overflow-x-auto">
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-weak text-xs text-n-slate-10">
          <th class="text-left font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.NAME') }}
          </th>
          <th class="text-left font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.STATUS') }}
          </th>
          <th class="text-right font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.SPEND') }}
          </th>
          <th class="text-right font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.IMPRESSIONS') }}
          </th>
          <th class="text-right font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.CLICKS') }}
          </th>
          <th class="text-right font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.LEADS') }}
          </th>
          <th class="text-right font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.REVENUE') }}
          </th>
          <th class="text-right font-medium px-4 py-2.5">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.ROAS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="campaign in props.campaigns"
          :key="campaign.id"
          class="border-b border-n-weak last:border-0 cursor-pointer hover:bg-n-alpha-2"
          @click="goToDetail(campaign)"
        >
          <td class="px-4 py-2.5 text-n-slate-12 font-medium">
            {{ campaign.name }}
          </td>
          <td class="px-4 py-2.5">
            <span
              class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{ campaign.status }}
            </span>
          </td>
          <td class="px-4 py-2.5 text-right text-n-slate-12">
            {{ brl(campaign.spend) }}
          </td>
          <td class="px-4 py-2.5 text-right text-n-slate-11">
            {{ formatFullNumber(campaign.impressions) }}
          </td>
          <td class="px-4 py-2.5 text-right text-n-slate-11">
            {{ formatFullNumber(campaign.clicks) }}
          </td>
          <td class="px-4 py-2.5 text-right text-n-slate-11">
            {{ formatFullNumber(campaign.leads) }}
          </td>
          <td class="px-4 py-2.5 text-right text-n-slate-12">
            {{ brl(campaign.revenue) }}
          </td>
          <td
            class="px-4 py-2.5 text-right font-medium"
            :class="roasClass(campaign.roas)"
          >
            {{ campaign.roas ? `${formatFullNumber(campaign.roas)}x` : '—' }}
          </td>
        </tr>
        <tr v-if="!props.campaigns.length">
          <td colspan="8" class="px-4 py-6 text-center text-sm text-n-slate-9">
            {{ t('META_CAMPAIGNS.CAMPAIGN_TABLE.NO_DATA') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
